import {
  cancelSigningRequest,
  deleteSigningRequest,
  getProviderSigningRequestStatus,
  getSignedDocuments,
  getSigningRequest,
  getSigningRequestStatusHistory,
  searchSigningRequest,
  sendParticipantReminder,
  updateSigningRequest,
  type ParticipantInDb,
  type SendYoutrustCancelRequest,
  type SignedVaultFileRevisionInDb,
  type SigningRequestInDb,
  type SigningRequestUpdate,
  type FilterableSearchQuery,
} from '@/api';
import { useAuthStore } from '@/stores/auth';

function extractErrorMessage(err: unknown, defaultMessage: string): string {
  if (err instanceof Error && err.message) {
    return err.message;
  }

  if (typeof err === 'object' && err !== null && 'detail' in err) {
    return String(err.detail);
  }

  return defaultMessage;
}

export const SignatureRequestService = {
  async search(
    query: FilterableSearchQuery,
    include_archived: boolean
  ): Promise<import('@/api').SigningRequestSearchResult> {
    try {
      const response = await searchSigningRequest({
        body: query,
        query: { include_archived },
      });

      if (!response.data) {
        throw new Error('Failed to load signature requests.');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to load signature requests. Please try again.'),
        { cause: error }
      );
    }
  },

  async get(signingRequestUid: string): Promise<import('@/api').SigningRequestInDb> {
    try {
      const response = await getSigningRequest({
        path: { signing_request_uid: signingRequestUid },
      });

      if (!response.data) {
        throw new Error('Failed to load signature request.');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to load signature request. Please try again.'),
        { cause: error }
      );
    }
  },

  async getProvider(signingRequestUid: string): Promise<string | null> {
    try {
      const response = await getProviderSigningRequestStatus({
        path: { signing_request_uid: signingRequestUid },
      });

      if (!response.data) {
        throw new Error('Failed to update signature request status.');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to update signature request status. Please try again.'),
        { cause: error }
      );
    }
  },

  async getStatusHistory(
    signingRequestUid: string
  ): Promise<import('@/api').SigningRequestStatusEntry[] | null> {
    try {
      const response = await getSigningRequestStatusHistory({
        path: { signing_request_uid: signingRequestUid },
      });

      if (!response.data) {
        return null;
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(
          error,
          'Failed to load signing request status history. Please try again.'
        ),
        { cause: error }
      );
    }
  },

  async getSignedDocuments(signingRequestUid: string): Promise<SignedVaultFileRevisionInDb[]> {
    try {
      const response = await getSignedDocuments({
        path: { signing_request_uid: signingRequestUid },
      });

      if (!response.data) {
        throw new Error('Failed to load signed documents.');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to load signed documents. Please try again.'),
        { cause: error }
      );
    }
  },

  async downloadCombinedSignedDocument(
    signingRequestUid: string,
    acknowledgeSignatureCleaning: boolean
  ): Promise<Blob> {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
    const apiUrl = import.meta.env.VITE_API_URL || '';
    const url =
      `${apiUrl}/v1/app/signing/${encodeURIComponent(signingRequestUid)}` +
      `/signed-documents/combined?acknowledge_signature_cleaning=${acknowledgeSignatureCleaning}`;

    const authStore = useAuthStore();
    const token = await authStore.user?.getIdToken();
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
      throw new Error(
        extractErrorMessage(
          { detail: `HTTP error: ${response.status}` },
          'Failed to download combined signed document. Please try again.'
        )
      );
    }

    return new Blob([await response.arrayBuffer()], { type: 'application/pdf' });
  },

  async downloadAuditTrail(signingRequestUid: string): Promise<Blob> {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- an empty/falsy value must use the fallback
    const apiUrl = import.meta.env.VITE_API_URL || '';
    const url = `${apiUrl}/v1/app/signing/${encodeURIComponent(signingRequestUid)}/audit-trail`;

    const authStore = useAuthStore();
    const token = await authStore.user?.getIdToken();
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    if (!response.ok) {
      throw new Error(
        extractErrorMessage(
          { detail: `HTTP error: ${response.status}` },
          'Failed to download audit trail. Please try again.'
        )
      );
    }

    return new Blob([await response.arrayBuffer()], { type: 'application/pdf' });
  },

  async cancel(
    signingRequestUid: string,
    request: SendYoutrustCancelRequest
  ): Promise<SigningRequestInDb> {
    try {
      const response = await cancelSigningRequest({
        path: { signing_request_uid: signingRequestUid },
        body: request,
      });

      if (!response.data) {
        throw new Error('Failed to cancel signature request.');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to cancel signature request. Please try again.'),
        { cause: error }
      );
    }
  },

  /**
   * Partial update of a signing request. Everything except `archived` is draft-only — the
   * backend refuses the update once the request has left DRAFT, since none of it propagates
   * to the provider.
   */
  async update(signingRequestUid: string, body: SigningRequestUpdate): Promise<SigningRequestInDb> {
    try {
      const response = await updateSigningRequest({
        path: { signing_request_uid: signingRequestUid },
        body,
      });

      if (!response.data) {
        throw new Error('Failed to update signature request.');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to update signature request. Please try again.'),
        { cause: error }
      );
    }
  },

  async setArchived(signingRequestUid: string, archived: boolean): Promise<SigningRequestInDb> {
    return this.update(signingRequestUid, { archived });
  },

  /** Discard a draft signing request outright, participants and document links included. */
  async remove(signingRequestUid: string): Promise<SigningRequestInDb> {
    try {
      const response = await deleteSigningRequest({
        path: { signing_request_uid: signingRequestUid },
      });

      if (!response.data) {
        throw new Error('Failed to delete signature request.');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to delete signature request. Please try again.'),
        { cause: error }
      );
    }
  },

  async sendParticipantReminder(
    signingRequestUid: string,
    participantUid: string
  ): Promise<ParticipantInDb> {
    try {
      const response = await sendParticipantReminder({
        path: {
          signing_request_uid: signingRequestUid,
          participant_uid: participantUid,
        },
      });

      if (!response.data) {
        throw new Error('Failed to send participant reminder.');
      }

      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, 'Failed to send participant reminder. Please try again.'),
        { cause: error }
      );
    }
  },
};
